import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import WorkflowDetailContainer from "modules/analyst/containers/WorkflowDetail";
import { Header } from "build-ts/metadata";
import { buildWorkflowTag } from "modules/chat/utils/chatUtils";
import AnalysisRunsViewerContainer from "modules/analyst/containers/AnalysisRunsViewer";

interface JobDetailViewProps {
  onBack: () => void;
  selectedJob?: Header;
  onJobNameChange?: (newName: string) => void;
}

const JobDetailView: React.FC<JobDetailViewProps> = ({ onBack, selectedJob, onJobNameChange }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [triggerNameSave, setTriggerNameSave] = useState<boolean>(false);
  const [darwinName, setDarwinName] = useState<string>("");

  // Editable job name — persisted via WorkflowFormContainer when triggerNameSave fires
  const [jobName, setJobName] = useState<string>(selectedJob?.name?.trim() || "");
  const [isEditingJobName, setIsEditingJobName] = useState(false);
  const [jobNameDraft, setJobNameDraft] = useState("");

  // Reset state when the selected job changes
  useEffect(() => {
    setActiveTab(0);
    setJobName(selectedJob?.name?.trim() || "");
    setIsEditingJobName(false);
    setJobNameDraft("");
  }, [selectedJob?.id]);

  const jobDisplayName = jobName || selectedJob?.name?.trim() || "New Workflow";

  const handleRunWorkflow = useCallback(() => {
    navigate("/chat/new", {
      state: {
        initialPrompt:
          `I want to run the workflow: ${buildWorkflowTag(darwinName, jobDisplayName)}. Fetch the details of the` +
          " workflow and run it. Ask me If there is any input required to run the workflow.",
        analystId: selectedJob?.owner_id,
      },
    });
  }, [navigate, darwinName, jobDisplayName, selectedJob?.owner_id]);

  const handleEditWorkflow = useCallback(() => {
    navigate("/chat/new", {
      state: {
        initialPrompt:
          `I want to edit the workflow: ${buildWorkflowTag(darwinName, jobDisplayName)}. Fetch the workflow` +
          " details, ask me what I want to edit and implement the changes across the workflow.",
        analystId: selectedJob?.owner_id,
      },
    });
  }, [navigate, darwinName, jobDisplayName, selectedJob?.owner_id]);

  const confirmJobNameEdit = useCallback(() => {
    const confirmed = jobNameDraft.trim() || jobDisplayName;
    setJobName(confirmed);
    setIsEditingJobName(false);
    // Notify the parent component that the job name has changed - this is used to update the name in breadcrumb
    onJobNameChange?.(confirmed);
    // Persist the new name for existing jobs
    if (selectedJob?.id) {
      setTriggerNameSave(true);
    }
  }, [jobNameDraft, jobDisplayName, selectedJob?.id, onJobNameChange]);

  return (
    <Box flex="1" minH={0} minW={0} bg="white" display="flex" flexDirection="column" overflow="hidden">
      {/* Chrome header: back, editable name, tabs, Run */}
      <HStack
        px={3}
        pb={0}
        pt={0.5}
        borderBottom="1px solid"
        borderColor="gray.200"
        align="flex-end"
        w="100%"
        spacing={3}
        minH="44px"
        flexShrink={0}
      >
        {/* Back + editable job name */}
        <HStack minW={0} flex="1" spacing={2} align="center">
          <IconButton
            aria-label="Back to workflows"
            icon={<Icon as={AppIcons.leftArrow} boxSize={4} />}
            variant="ghost"
            size="sm"
            color="gray.700"
            _hover={{ bg: "gray.100" }}
            onClick={onBack}
            flexShrink={0}
          />
          <Icon as={AppIcons.workflow} boxSize={5} color="purple.700" flexShrink={0} aria-hidden />

          {isEditingJobName ? (
            <HStack flex="1" minW={0} spacing={2} align="center">
              <Input
                flex="1"
                minW={0}
                size="md"
                variant="flushed"
                fontWeight="600"
                color="gray.800"
                autoFocus
                value={jobNameDraft}
                onChange={e => setJobNameDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") confirmJobNameEdit();
                  else if (e.key === "Escape") setIsEditingJobName(false);
                }}
                borderColor="purple.400"
                _focusVisible={{ borderColor: "purple.500", boxShadow: "none" }}
              />
              <IconButton
                aria-label="Confirm job name"
                icon={<Icon as={AppIcons.check} boxSize={4} color="green.500" />}
                size="xs"
                variant="ghost"
                colorScheme="green"
                flexShrink={0}
                onMouseDown={e => e.preventDefault()}
                onClick={confirmJobNameEdit}
              />
            </HStack>
          ) : (
            <HStack flex="1" minW={0} spacing={1} align="center">
              <Text fontSize="md" fontWeight="700" color="gray.800" noOfLines={1} mb={0} minW={0} textAlign="left">
                {jobDisplayName}
              </Text>
              <IconButton
                aria-label="Edit workflow name"
                icon={<Icon as={AppIcons.edit} boxSize={3.5} />}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                color="gray.400"
                _hover={{ color: "purple.600", bg: "purple.50" }}
                flexShrink={0}
                onClick={() => {
                  setJobNameDraft(jobDisplayName);
                  setIsEditingJobName(true);
                }}
              />
            </HStack>
          )}
        </HStack>

        {/* Process / Guidance / Past runs tabs + Run button */}
        <HStack spacing={3} flexShrink={0} align="flex-end" pb={0}>
          <Tabs variant="line" colorScheme="purple" index={activeTab} onChange={setActiveTab} size="sm">
            <TabList borderBottom="none" gap={0} minH="auto" aria-label="Workflow sections">
              <Tab
                fontWeight="600"
                py={2}
                px={3}
                _selected={{ fontWeight: "700", borderBottomWidth: "3px", borderColor: "purple.700" }}
              >
                <HStack as="span" spacing={2}>
                  <Icon as={AppIcons.workflow} boxSize={5} flexShrink={0} />
                  <Text as="span" mb={0} fontSize="md">
                    Workflow
                  </Text>
                </HStack>
              </Tab>
              <Tab
                fontWeight="600"
                py={2}
                px={3}
                _selected={{ fontWeight: "700", borderBottomWidth: "3px", borderColor: "purple.700" }}
              >
                <HStack as="span" spacing={2}>
                  <Icon as={AppIcons.codeAlt} boxSize={5} flexShrink={0} />
                  <Text as="span" mb={0} fontSize="md">
                    Guidance
                  </Text>
                </HStack>
              </Tab>
              <Tab
                fontWeight="600"
                py={2}
                px={3}
                _selected={{ fontWeight: "700", borderBottomWidth: "3px", borderColor: "purple.700" }}
              >
                <HStack as="span" spacing={2}>
                  <Icon as={AppIcons.history} boxSize={4} flexShrink={0} />
                  <Text as="span" mb={0} fontSize="md">
                    Past runs
                  </Text>
                </HStack>
              </Tab>
            </TabList>
            {/* Hidden panels — content is rendered below via CSS display toggling */}
            <TabPanels
              display="none"
              position="absolute"
              visibility="hidden"
              aria-hidden
              h={0}
              w={0}
              overflow="hidden"
              p={0}
              m={0}
            >
              <TabPanel p={0} />
              <TabPanel p={0} />
              <TabPanel p={0} />
            </TabPanels>
          </Tabs>

          {selectedJob?.id && (
            <HStack spacing={2} mb="2px">
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                fontWeight="600"
                fontSize="md"
                onClick={handleEditWorkflow}
                leftIcon={<Icon as={AppIcons.edit} boxSize={4} />}
              >
                Edit
              </Button>
              <Button
                size="sm"
                colorScheme="purple"
                bg="purple.700"
                color="white"
                _hover={{ bg: "purple.600" }}
                _active={{ bg: "purple.800" }}
                fontWeight="600"
                fontSize="md"
                onClick={handleRunWorkflow}
                leftIcon={<Icon as={AppIcons.runWorkflow} boxSize={4} />}
              >
                Run
              </Button>
            </HStack>
          )}
        </HStack>
      </HStack>

      {/* Content */}
      <Box flex="1" overflow="hidden" display="flex" flexDirection="column" minH={0}>
        <Box display={activeTab === 2 ? "none" : "flex"} flex="1" minH={0} overflow="hidden" flexDirection="column">
          <WorkflowDetailContainer
            jobName={jobName}
            setJobName={setJobName}
            selectedJob={selectedJob}
            triggerNameSave={triggerNameSave}
            setTriggerNameSave={setTriggerNameSave}
            jobFormView={activeTab === 1 ? "guidance" : "process"}
            setDarwinName={setDarwinName}
          />
        </Box>
        <Box
          display={activeTab === 2 ? "flex" : "none"}
          flex="1"
          minH={0}
          overflow="hidden"
          flexDirection="column"
          px={2}
        >
          <Box flex="1" minH={0} overflowY="auto" px={6} py={5}>
            {activeTab === 2 && <AnalysisRunsViewerContainer selectedJob={selectedJob} hideRunDetails={true} />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default JobDetailView;
